/// <amd-module name="Agile/Common/Utils/CapacityDateUtils" />

import { addDays } from "Agile/Common/Utils/Date";
import { DayOfWeek } from "Agile/Contracts/Common";
import { DateRange } from "Agile/Contracts/Date";

export function calculateTotalNetDaysOff(
    daysOffList: DateRange[],
    weekends: number[],
    teamDaysOff?: DateRange[],
    iterationStartDate?: Date,
    iterationEndDate?: Date
): number {
    return daysOffList.reduce(
        (sumOfDays: number, daysOff: DateRange) =>
            sumOfDays + calculateNetDaysOff(daysOff, weekends, teamDaysOff, iterationStartDate, iterationEndDate),
        0
    );
}

export function calculateNetDaysOff(
    daysOff: DateRange,
    weekends: number[],
    teamDaysOff?: DateRange[],
    iterationStartDate?: Date,
    iterationEndDate?: Date
): number {
    if (!daysOff || !daysOff.start || !daysOff.end) {
        return 0;
    }

    let netDaysOff = 0;
    let date = daysOff.start;
    while (date <= daysOff.end) {
        if (!isWeekend(date, weekends) && isWithinIteration(date, iterationStartDate, iterationEndDate) && !isWithinDaysOff(date, teamDaysOff)) {
            netDaysOff++;
        }

        date = addDays(date, 1);
    }

    return netDaysOff;
}

export function calculateRemainingWorkingDays(
    currentDate: Date,
    weekends: number[],
    userDaysOff: DateRange[],
    iterationStartDate?: Date,
    iterationEndDate?: Date,
    teamDaysOff?: DateRange[]
): number {
    if (!iterationEndDate || !iterationStartDate) {
        return 0;
    } else {
        let totalRemainingWorkingDays = 0;
        let date = getCurrentDateInIteration(currentDate, iterationStartDate);
        while (date <= iterationEndDate) {
            if (
                !isWeekend(date, weekends) &&
                isWithinIteration(date, iterationStartDate, iterationEndDate) &&
                !isWithinDaysOff(date, teamDaysOff) &&
                !isWithinDaysOff(date, userDaysOff)
            ) {
                totalRemainingWorkingDays++;
            }

            date = addDays(date, 1);
        }

        return totalRemainingWorkingDays;
    }
}

function getCurrentDateInIteration(currentDate: Date, iterationStartDate?: Date): Date {
    // If the current date is greater than the iteration start date, then use it.  When it is
    // not greater than the iteration start date the iteration has not started and we should
    // use the start date of the iteration for the date calculations since it is a future iteration.
    if (!iterationStartDate) {
        return currentDate;
    } else {
        return currentDate > iterationStartDate ? currentDate : iterationStartDate;
    }
}

function isWithinDaysOff(date: Date, daysOff?: DateRange[]): boolean {
    if (!daysOff) {
        return false;
    }

    for (const dayoff of daysOff) {
        if (date >= dayoff.start && date <= dayoff.end) {
            return true;
        }
    }

    return false;
}

function isWithinIteration(date: Date, iterationStartDate?: Date, iterationEndDate?: Date): boolean {
    if (!iterationStartDate || !iterationEndDate) {
        return true;
    }

    return date >= iterationStartDate && date <= iterationEndDate;
}

function isWeekend(date: Date, weekends: number[]): boolean {
    const day = date.getDay();
    return weekends.findIndex(d => d === day) !== -1;
}

/**  Get the number of working days between start date and end date, including both of them.
 *   e.g. --
 *   1. startDate = 20th June Saturday, endDate = 21st June Sunday => return 0
 *   2. startDate = 22nd June Monday, endDate = 22nd June Monday   => return 1
 *   3. startDate = 22nd June Monday, endDate = 23rd June Tuesday  => return 2
 *   4. startDate = 22nd June Monday, endDate = 29th June Monday   => return 6
 * @param startDate The date to start from
 * @param endDate The date to end at
 * @param weekends Array of values (0-6) that represent days considered to be weekends
 * @returns Returns the number of working days between startDate and endDate
 */
export function getNumberOfWorkingDays(startDate: Date, endDate: Date, weekends: any[], teamDaysOff: DateRange[]): number {
    let numWorkingDays = 0,
        nextDate = new Date(startDate.getTime());

    while (nextDate <= endDate) {
        // Only count day if it is a working day (not weekend) and the team is working (not a team day off)
        if (weekends.indexOf(nextDate.getDay()) === -1 && !hasOverlapWithTeamDaysOff(nextDate, teamDaysOff)) {
            numWorkingDays += 1;
        }
        nextDate.setDate(nextDate.getDate() + 1);
    }

    return numWorkingDays;
}

/** Get the number of working days between today and end date, including the end date. Return 0 if today is not in the current iteration.
 *  End date is expected to be passed in as a UTC time.
 *
 *  This method uses end date in UTC and today in local time. Iteration dates come from the server in UTC
 *  as our way of attempting to ignore the time. When it gets to the client javascript automatically converts it to local time.
 *  To return it back to our "date only" format we convert it to UTC - which should be a date at 00:00 hours.
 *  We then want to compare that date to local time. Local time must be stripped of any hours, minutes, etc, otherwise the same
 *  date at different hours will not be equivalent.
 *
 *  For example, if today is 10/10/2017 in Seattle (UTC -8). My iteration end date is 10/15/2017
 *  (stored on server as 10/15/2017 00:00 UTC), on the client end date will be converted to 10/14/2017 18:00.
 *  Now we need to compare today in Seattle (10/10/2017 at 00:00) to the end date converted back to UTC (10/15/2017 00:00)
 *  in order to accurately know if today is in the iteration. This example would return 6 working days left (no weekends).
 *
 * @param endDate The date to end at in UTC
 * @param weekends Array of values (0-6) that represent days considered to be weekends
 * @returns Returns the number of working days between startDate and endDate
 */
export function getNumberWorkingDaysLeft(endDate: Date, weekends: any[], teamDaysOff: DateRange[]): number {
    // Get today in local time with no hours so that we are just comparing dates.
    // Without this 10/1/17 2:00 is greater than 10/1/17 00:00.  We want these to be equivalent.

    return getNumberOfWorkingDays(getTodayInLocalTimeWithNoHours(), endDate, weekends, teamDaysOff);
}

/**
 * Return today (in local time) with no hours, minutes, seconds, milliseconds.
 */
export function getTodayInLocalTimeWithNoHours(): Date {
    const today = new Date(Date.now());
    return stripTimeFromDate(today);
}

/**
 * Determine if the iteration includes today.  Expecting the dates in UTC time. Calculates using today local time.
 * (i.e. all dates should have time 00:00.  This way the date is true to what is stored server side)
 * @param startDate - Start date of iteration in UTC.
 * @param endDate - End date of iteration in UTC.
 */
export function isTodayInSprint(startDate: Date, endDate: Date): boolean {
    // Zero out the time so that we are just comparing dates.
    // Without this 10/1/17 2:00 is greater than 10/1/17 00:00.  We want these to be equivalent.
    const today = getTodayInLocalTimeWithNoHours();
    return startDate <= today && endDate >= today;
}

/**
 * Strip the time from the given date (return a new date) such that the new date is of 12:00:00 AM
 */
export function stripTimeFromDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
/**
 * Check if the provided date is a team day off
 * @param date Date to check
 * @param teamDaysOff Set of date ranges that the team is taking off
 */
export function hasOverlapWithTeamDaysOff(date: Date, teamDaysOff: DateRange[]): boolean {
    for (const teamDaysOffRange of teamDaysOff) {
        if (date >= teamDaysOffRange.start && date <= teamDaysOffRange.end) {
            return true;
        }
    }

    return false;
}

export function getWeekendsFromWorkingDays(workingDays: DayOfWeek[]): DayOfWeek[] {
    const weekends = [
        DayOfWeek.Sunday,
        DayOfWeek.Monday,
        DayOfWeek.Tuesday,
        DayOfWeek.Wednesday,
        DayOfWeek.Thursday,
        DayOfWeek.Friday,
        DayOfWeek.Saturday
    ].filter(day => workingDays.indexOf(day) === -1);
    return weekends;
}
